import { fg } from '@confluence/feature-gating';
import {
	deserializeState,
	useGetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus';
import { useIsNewUser } from '@confluence/targeting/entry-points/useIsNewUser';
import { useSiteUserTraits } from '@confluence/targeting/entry-points/useSiteUserTraits';
import { CREATE_WITH_ROVO_ANNOUNCEMENT_SPLASH } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

const TRAIT_HAS_USED_CREATE_WITH_ROVO = 'confluence_has_used_create_with_rovo';

export type UseIsEligibleRovoAnnouncement = {
	isEligible: boolean;
	hasSeenSplashModal: boolean;
	loading: boolean;
	error?: Error;
};

/**
 * Checks if the current user is eligible to see the Rovo announcement splash.
 *
 * Exclusion criteria:
 * - feature flag disabled
 * - Rovo LLM not enabled
 * - User is not licensed
 * - New users (https://hello.atlassian.net/wiki/spaces/activation/pages/5471341463/Eng+Goodness+Onboarding+vs.+Changeboarding+Targeting+Traits)
 * - Users who have already seen the announcement
 * - Users who have already used create with Rovo
 */
export const useIsEligibleRovoAnnouncement = (): UseIsEligibleRovoAnnouncement => {
	const { isRovoLLMEnabled } = useSessionData();
	const isLicensed = useIsLicensed();

	const basicEligibilityMet = isRovoLLMEnabled && isLicensed && fg('aifc_onboarding_ga_enabled');
	const {
		data,
		loading: isLoadingHasSeenModal,
		error: errorHasSeenModal,
	} = useGetOnboardingState(
		Object.values(CREATE_WITH_ROVO_ANNOUNCEMENT_SPLASH),
		!basicEligibilityMet,
	);
	const { hasSeenSplashModal } = deserializeState(data);

	const shouldCheckUserTargeting = basicEligibilityMet && !hasSeenSplashModal;

	const {
		isNewUser,
		loading: isLoadingNewUser,
		error: errorNewUser,
	} = useIsNewUser({}, !shouldCheckUserTargeting);

	const {
		data: dataTraits,
		loading: isLoadingTraits,
		error: errorTraits,
	} = useSiteUserTraits({ skipBootstrap: !shouldCheckUserTargeting });
	const hasUsedCreateWithRovo = dataTraits?.attributes?.find(
		({ name }) => name === TRAIT_HAS_USED_CREATE_WITH_ROVO,
	)?.value;

	const loading = isLoadingHasSeenModal || isLoadingNewUser || isLoadingTraits;
	const error = errorNewUser || errorTraits || errorHasSeenModal;

	if (loading) {
		return {
			isEligible: false,
			hasSeenSplashModal: !!hasSeenSplashModal,
			loading: true,
		};
	}

	if (error) {
		return {
			isEligible: false,
			hasSeenSplashModal: !!hasSeenSplashModal,
			loading: false,
			error,
		};
	}

	const isEligible =
		basicEligibilityMet && !isNewUser && !hasSeenSplashModal && !hasUsedCreateWithRovo;

	return {
		isEligible,
		hasSeenSplashModal: !!hasSeenSplashModal,
		loading: false,
	};
};
